import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API } from '../config/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Crown, CheckCircle2, Loader2, ArrowRight, Sparkles } from 'lucide-react';

export default function StudioPlusSuccessPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { getAuthHeaders } = useAuth();
  const sessionId = params.get('session_id');
  const [status, setStatus] = useState('polling'); // polling | paid | failed
  const [tries, setTries] = useState(0);

  // Poll payment status
  useEffect(() => {
    if (!sessionId || status === 'paid' || status === 'failed') return;
    const t = setTimeout(async () => {
      try {
        const res = await fetch(`${API}/payments/status/${sessionId}`);
        if (res.ok) {
          const data = await res.json();
          if (data.payment_status === 'paid') {
            setStatus('paid');
            return;
          }
        }
        // Fallback: check studio-plus/status
        const s = await fetch(`${API}/studio-plus/status`, { headers: getAuthHeaders() });
        if (s.ok) {
          const sd = await s.json();
          if (sd.is_studio_plus) {
            setStatus('paid');
            return;
          }
        }
      } catch { /* noop */ }
      if (tries >= 10) {
        setStatus('failed');
      } else {
        setTries(t2 => t2 + 1);
      }
    }, 2000);
    return () => clearTimeout(t);
  }, [sessionId, tries, status, getAuthHeaders]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-gradient-to-br from-amber-500/10 via-fuchsia-500/10 to-violet-600/10 pointer-events-none" />
      <Card className="relative max-w-md w-full p-8 text-center" data-testid="studio-plus-success">
        {status === 'polling' && (
          <>
            <Loader2 className="w-12 h-12 mx-auto text-fuchsia-400 animate-spin mb-4" />
            <h1 className="text-2xl font-black mb-2">Activating Studio+…</h1>
            <p className="text-sm text-muted-foreground">Confirming payment with Stripe. This usually takes a few seconds.</p>
          </>
        )}
        {status === 'paid' && (
          <>
            <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-amber-400 to-fuchsia-500 flex items-center justify-center shadow-xl mb-4">
              <Crown className="w-8 h-8 text-white" />
            </div>
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 text-[10px] font-black uppercase tracking-widest mb-3">
              <CheckCircle2 className="w-3 h-3" /> Payment Confirmed
            </div>
            <h1 className="text-3xl font-black mb-2">Welcome to Studio+</h1>
            <p className="text-sm text-muted-foreground mb-6">
              Customize your profile, unlock advanced analytics, and stand out with the Studio+ crown.
            </p>
            <div className="grid grid-cols-3 gap-2 mb-6 text-[10px] uppercase font-black tracking-widest">
              <div className="p-2 rounded-lg bg-muted/40 flex flex-col items-center gap-1 text-fuchsia-400">
                <Sparkles className="w-4 h-4" /> Customize
              </div>
              <div className="p-2 rounded-lg bg-muted/40 flex flex-col items-center gap-1 text-amber-400">
                <Crown className="w-4 h-4" /> Crown Badge
              </div>
              <div className="p-2 rounded-lg bg-muted/40 flex flex-col items-center gap-1 text-emerald-400">
                <CheckCircle2 className="w-4 h-4" /> Priority
              </div>
            </div>
            <Button
              onClick={() => navigate('/studio-plus?tab=customize')}
              className="w-full bg-gradient-to-r from-amber-500 to-fuchsia-500 hover:from-amber-600 hover:to-fuchsia-600 text-white shadow-lg"
              data-testid="studio-plus-customize-now"
            >
              Customize My Profile <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
            <Button variant="link" onClick={() => navigate('/dashboard')} className="w-full mt-2">
              Or go to dashboard
            </Button>
          </>
        )}
        {status === 'failed' && (
          <>
            <h1 className="text-2xl font-black mb-2">Still processing…</h1>
            <p className="text-sm text-muted-foreground mb-4">
              Payment is confirmed by Stripe but your account is still syncing. This can take up to a minute.
            </p>
            <Button onClick={() => navigate('/studio-plus')} className="w-full">
              Go to Studio+
            </Button>
          </>
        )}
      </Card>
    </div>
  );
}
